import { tv } from "@ui/lib/tv";

/**
 * The contents of the mobile action sheet. The surface itself (travel, scrim,
 * gutters, safe area) is `MobileSheet`; these are the two platform-native
 * option-list treatments inside it:
 *
 * - **iOS** (alert-style): a grouped white card (20px radius, centered rows,
 *   hairline dividers) plus a detached Cancel pill (40px radius).
 * - **Android** (Material 3): left-aligned rows on the sheet's own fill.
 *
 * Destructive rows use pink/500 on both. The iOS group's 0.08 shadow is a
 * deliberately lighter elevation than `--shadow-sheet` — the card floats in
 * gutters — so it stays a local literal.
 */
export const actionSheet = tv({
  slots: {
    panel: "",
    dialog: "flex flex-col outline-none",
    group: "flex flex-col bg-white",
    menu: "flex flex-col outline-none",
    item: "relative flex h-14 cursor-pointer select-none items-center text-base outline-none transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:text-neutral-300",
    check: "shrink-0",
    cancel:
      "flex h-14 w-full cursor-pointer items-center justify-center rounded-full bg-white font-semibold text-base text-neutral-900 outline-none transition-colors data-[focus-visible]:bg-neutral-50 data-[pressed]:bg-neutral-100",
    title: "text-neutral-500",
  },
  variants: {
    platform: {
      ios: {
        dialog: "gap-3",
        group: "overflow-hidden rounded-3xl shadow-[0px_3px_20px_0px_rgba(0,0,0,0.08)]",
        // focus-visible (not focused): RAC autofocuses the selected item on
        // open — a plain focused fill would read as a stuck gray row.
        item: "justify-center px-4 text-center text-neutral-900 data-[focus-visible]:bg-neutral-50 data-[pressed]:bg-neutral-100 [&:not(:last-child)]:border-neutral-100 [&:not(:last-child)]:border-b",
        check: "absolute right-4 text-neutral-900",
        title: "flex min-h-12 items-center justify-center border-neutral-100 border-b px-4 text-center text-sm",
      },
      android: {
        menu: "py-2",
        item: "justify-start gap-3 px-4 text-neutral-900 data-[focus-visible]:bg-neutral-100 data-[pressed]:bg-neutral-200",
        check: "ml-auto text-neutral-600",
        title: "px-4 pt-1 pb-2 text-sm",
      },
    },
  },
});
